// Implements: F-VER-004, F-VER-006, F-CNV-015, F-UIX-009, F-UIX-010, F-UIX-013, F-UIX-017
/**
 * One version of a mockup: the container that owns its query and its promote.
 *
 * The three states are components (F-UIX-010). Promoting is not confirmed: it
 * only moves a pointer, creates nothing and is undone by promoting another
 * version. "Continue from this version" moves the same pointer through the
 * conversation, which also leaves its trace in the thread; it is offered to
 * owners and editors only. The notice says what the server answered, in its own words for the
 * enumerated codes and in ours for any other.
 *
 * Below the version sit the versions before and after it (F-UIX-017), as links and as
 * Alt+Shift+Arrow. They come from the tree, which lists only what the person may see; a
 * tree that fails to load costs the page its steps, never the version itself.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { useNavigate } from "react-router";

import { asApiError } from "../../../shared/api/asApiError";
import { useTranslation } from "../../../shared/i18n";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { LoadingState } from "../../../shared/ui/molecules/LoadingState";
import { useToast } from "../../../shared/ui/molecules/ToastProvider";
import { TURN_REFUSAL_CODES, userMessage } from "../../../shared/ui/molecules/userMessage";
import { saveBlob } from "../../../shared/ui/saveBlob";
import { useKeyShortcut } from "../../../shared/ui/useKeyShortcut";
import { defaultDownloadName, downloadVersionFile } from "../api/versionsApi";
import { VersionDetail } from "../components/VersionDetail";
import type { DownloadKey } from "../components/VersionDetail";
import {
  NEXT_VERSION_SHORTCUT,
  PREVIOUS_VERSION_SHORTCUT,
  VersionStepper,
  versionPath,
} from "../components/VersionStepper";
import { versionNeighbours } from "../components/versionSteps";
import { useContinueFromVersion } from "../hooks/useContinueFromVersion";
import { usePromoteVersion } from "../hooks/usePromoteVersion";
import { useVersionDetail } from "../hooks/useVersionDetail";
import { useVersionTree } from "../hooks/useVersionTree";

export interface VersionDetailContainerProps {
  readonly mockupId: string;
  readonly versionId: string;
}

export function VersionDetailContainer({
  mockupId,
  versionId,
}: VersionDetailContainerProps): ReactElement {
  const { t } = useTranslation();
  const detail = useVersionDetail(mockupId, versionId);
  const promote = usePromoteVersion();
  const continueFrom = useContinueFromVersion();
  const { push } = useToast();
  const [promoting, setPromoting] = useState(false);
  const [continuing, setContinuing] = useState(false);
  const [downloading, setDownloading] = useState<DownloadKey | null>(null);
  // A promote answers for the version it was pressed on: if the page has moved
  // on, its notice would be about another version.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // The steps to the versions before and after this one (F-UIX-017).
  const tree = useVersionTree(mockupId);
  const steps =
    tree.status === "success"
      ? versionNeighbours(tree.data.nodes, versionId)
      : { previous: null, next: null };
  const navigate = useNavigate();
  const previousId = steps.previous?.id ?? null;
  const nextId = steps.next?.id ?? null;
  const toPrevious = useCallback((): void => {
    if (previousId !== null) void navigate(versionPath(mockupId, previousId));
  }, [navigate, mockupId, previousId]);
  const toNext = useCallback((): void => {
    if (nextId !== null) void navigate(versionPath(mockupId, nextId));
  }, [navigate, mockupId, nextId]);
  useKeyShortcut(PREVIOUS_VERSION_SHORTCUT, previousId !== null, toPrevious);
  useKeyShortcut(NEXT_VERSION_SHORTCUT, nextId !== null, toNext);

  if (detail.status === "pending") return <LoadingState label={t("common.state.loading")} />;
  if (detail.status === "error") {
    return (
      <ErrorState
        error={detail.error}
        onRetry={() => {
          detail.refetch();
        }}
      />
    );
  }

  const sequenceNumber = detail.data.version.sequence_number;
  const onDownload = async (key: DownloadKey): Promise<void> => {
    setDownloading(key);
    try {
      const file = await downloadVersionFile(mockupId, versionId, key);
      saveBlob(file.blob, file.filename ?? defaultDownloadName(key, sequenceNumber));
    } catch (cause) {
      push({
        tone: "danger",
        message: userMessage(asApiError(cause), t("version.download.failed")),
      });
    } finally {
      setDownloading(null);
    }
  };

  const onPromote = async (): Promise<void> => {
    setPromoting(true);
    const refusal = await promote(mockupId, versionId);
    if (!mounted.current) return;
    setPromoting(false);
    push(
      refusal === null
        ? { tone: "success", message: t("version.promoted") }
        : { tone: "danger", message: userMessage(refusal, t("version.promote.failed")) },
    );
  };

  const onContinue = async (): Promise<void> => {
    setContinuing(true);
    const refusal = await continueFrom(mockupId, versionId);
    if (!mounted.current) return;
    setContinuing(false);
    push(
      refusal === null
        ? { tone: "success", message: t("version.continued") }
        : {
            tone: "danger",
            // A busy mockup (`409`) is written for the person by the server.
            message: userMessage(refusal, t("version.continue.failed"), TURN_REFUSAL_CODES),
          },
    );
  };

  return (
    <>
      <VersionDetail
        version={detail.data.version}
        isCurrent={detail.data.isCurrent}
        promoting={promoting}
        onPromote={() => {
          void onPromote();
        }}
        canEdit={detail.data.canEdit}
        continuing={continuing}
        onContinue={() => {
          void onContinue();
        }}
        downloading={downloading}
        onDownload={(key) => {
          void onDownload(key);
        }}
      />
      <VersionStepper mockupId={mockupId} previous={steps.previous} next={steps.next} />
    </>
  );
}
